// R2：只控制 AIRI 原生运行时的准入和生命周期，不复制 Chat/Hearing/ACT 语义。
export function createHoloNativeActivationController({
  authority,
  authStore,
  cardStore,
  chatStore,
  voiceStore,
  orchestrator,
  resolveSessionCharacter,
  isLeader,
  isForeground,
  onNativeStateChange = () => {},
  now = () => Date.now(),
  scheduleInterval = (callback, ms) => setInterval(callback, ms),
  cancelInterval = handle => clearInterval(handle),
  renewIntervalMs = 10_000,
} = {}) {
  if (typeof chatStore?.onBeforeSend !== 'function')
    throw new TypeError('native Chat onBeforeSend hook is required')
  if (typeof resolveSessionCharacter !== 'function')
    throw new TypeError('native Chat Session character resolver is required')
  if (typeof isLeader !== 'function' || typeof isForeground !== 'function')
    throw new TypeError('leader and foreground predicates are required')
  if (typeof authority?.renew !== 'function' || typeof authority?.verifyCurrent !== 'function')
    throw new TypeError('native runtime lease authority is required')
  if (typeof voiceStore?.startListening !== 'function' || typeof voiceStore?.stopListening !== 'function')
    throw new TypeError('native AIRI Hearing controls are required')
  if (typeof orchestrator?.initialize !== 'function' || typeof orchestrator?.dispose !== 'function')
    throw new TypeError('native AIRI Character Orchestrator controls are required')

  let disposed = false
  let admitted = false
  let nativeReady = false
  let epoch = 0
  let stopping = null
  let stopBeforeSend = null
  let timer = null

  function eligible() {
    const lease = authority.runtimeLease
    return !disposed
      && authStore?.isAuthenticated === true
      && lease?.user_id === authStore?.userId
      && isLeader()
      && isForeground()
      && authority.isActive === true
      && typeof authority.characterId === 'string'
      && authority.characterId === cardStore?.activeCardId
      && lease?.character_id === authority.characterId
      && lease?.client_runtime_id === authority.clientRuntimeId
      && Number.isFinite(lease?.expires_at)
      && lease.expires_at > now()
  }

  function closeAdmission() {
    admitted = false
    // 先同步卸载 SpeechOutput 宿主，之后才异步取消和释放旧回合。
    onNativeStateChange(false)
    epoch += 1
  }

  async function stopNative() {
    closeAdmission()
    if (stopping)
      return stopping
    if (!nativeReady)
      return

    nativeReady = false
    stopping = (async () => {
      // 先关 Chat 准入，再终止已有语音、消息和编排；清理失败不能重新打开准入。
      const turns = Array.isArray(voiceStore.activeTurns) ? [...voiceStore.activeTurns] : []
      try {
        await chatStore.cancelPendingSends?.()
      }
      catch {}
      try {
        if (turns.length) {
          // 原生 Voice interrupt 返回 { done }；等待终止回执，避免旧音频仍在播放时重启新宿主。
          const interruption = voiceStore.interrupt?.(turns, 'holo_runtime_authority_lost')
          await interruption?.done
        }
      }
      catch {}
      try {
        await voiceStore.stopListening()
      }
      catch {}
      try {
        orchestrator.dispose()
      }
      catch {}
    })()

    try {
      await stopping
    }
    finally {
      stopping = null
    }
  }

  async function sync() {
    if (!eligible()) {
      await stopNative()
      return false
    }
    if (stopping)
      await stopping
    if (!eligible())
      return false
    if (nativeReady) {
      admitted = true
      onNativeStateChange(true)
      return true
    }

    const ticket = ++epoch
    try {
      orchestrator.initialize()
      nativeReady = true
      voiceStore.startListening()
      if (ticket !== epoch || !eligible()) {
        await stopNative()
        return false
      }
      admitted = true
      onNativeStateChange(true)
      return true
    }
    catch (error) {
      await stopNative()
      throw error
    }
  }

  async function renewOnce() {
    if (!eligible()) {
      await stopNative()
      return false
    }
    try {
      const ok = await authority.renew()
      if (!ok || !eligible()) {
        await stopNative()
        return false
      }
      return await sync()
    }
    catch {
      await stopNative()
      return false
    }
  }

  function start() {
    if (disposed)
      throw new Error('holo_activation_controller_disposed')
    if (stopBeforeSend)
      return
    stopBeforeSend = chatStore.onBeforeSend(async (_message, context) => {
      // 发送瞬间再检查租约、前台及 Leader，防止定时器暂停后的越权请求。
      if (!admitted || !eligible())
        throw new Error('holo_character_runtime_not_active')
      // The native Chat hook is async: check the trusted account-wide lease
      // before starting a new provider turn, not only at the renewal tick.
      if (!(await authority.verifyCurrent()) || !admitted || !eligible()) {
        await stopNative()
        throw new Error('holo_character_runtime_not_active')
      }
      // Native Chat may retain sessions for several characters. The active lease is not
      // authority to send on a different Character's session, including after a card switch.
      const sessionId = context?.sessionId
      if (!sessionId || resolveSessionCharacter(sessionId) !== authority.characterId)
        throw new Error('holo_character_session_not_authorized')
    })
    timer = scheduleInterval(() => {
      void renewOnce()
    }, renewIntervalMs)
  }

  async function dispose() {
    if (disposed)
      return
    closeAdmission()
    disposed = true
    if (timer !== null)
      cancelInterval(timer)
    timer = null
    stopBeforeSend?.()
    stopBeforeSend = null
    await stopNative()
  }

  return Object.freeze({
    start,
    sync,
    renewOnce,
    stopNative,
    dispose,
    canSend: () => admitted && eligible(),
  })
}
